
import React, { useState, useEffect } from 'react';
import { AppState, UserRole, League, Market, Player } from '../types.ts';
import { Plus, Trophy, User as UserIcon, Tag, ShieldCheck, Power, PowerOff, Target, ListChecks, ChevronDown, AlertCircle, Edit2, Check, X } from 'lucide-react';

interface AdminPanelProps {
  state: AppState;
  onAddLeague: (name: string) => Promise<boolean>;
  onAddPlayer: (name: string, leagueId: string) => Promise<boolean>;
  onAddMarket: (name: string) => Promise<boolean>;
  onUpdateLeague: (id: string, name: string) => void;
  onUpdatePlayer: (id: string, name: string, leagueId: string) => void;
  onUpdateMarket: (id: string, name: string) => void;
  onToggleUserStatus: (userId: string) => void;
  onUpdateUserRole: (userId: string, role: UserRole) => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({
  state, onAddLeague, onAddPlayer, onAddMarket,
  onUpdateLeague, onUpdatePlayer, onUpdateMarket,
  onToggleUserStatus, onUpdateUserRole
}) => {
  const [leagueName, setLeagueName] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerLeagueId, setPlayerLeagueId] = useState('');
  const [marketName, setMarketName] = useState('');

  // Estados de Erro
  const [leagueError, setLeagueError] = useState('');
  const [playerError, setPlayerError] = useState('');
  const [marketError, setMarketError] = useState('');

  // Estados de Edição
  const [editingLeagueId, setEditingLeagueId] = useState<string | null>(null);
  const [editLeagueName, setEditLeagueName] = useState('');

  const [editingMarketId, setEditingMarketId] = useState<string | null>(null);
  const [editMarketName, setEditMarketName] = useState('');

  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [editPlayerName, setEditPlayerName] = useState('');
  const [editPlayerLeagueId, setEditPlayerLeagueId] = useState('');

  // Estados de Busca
  const [userSearch, setUserSearch] = useState('');
  const [leagueSearch, setLeagueSearch] = useState('');
  const [marketSearch, setMarketSearch] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');

  useEffect(() => {
    if (state.leagues.length > 0 && !playerLeagueId) {
      setPlayerLeagueId(state.leagues[0].id);
    }
  }, [state.leagues, playerLeagueId]);

  const handleAddLeagueLocal = async () => {
    setLeagueError('');
    const trimmed = leagueName.trim();
    if (!trimmed) return;
    const exists = state.leagues.some(l => l.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) { setLeagueError('Esta liga já está cadastrada.'); return; }
    const success = await onAddLeague(trimmed);
    if (success) setLeagueName('');
  };

  const handleAddMarketLocal = async () => {
    setMarketError('');
    const trimmed = marketName.trim();
    if (!trimmed) return;
    const exists = state.markets.some(m => m.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) { setMarketError('Este mercado já está cadastrado.'); return; }
    const success = await onAddMarket(trimmed);
    if (success) setMarketName('');
  };

  const handleAddPlayerLocal = async () => {
    setPlayerError('');
    const trimmed = playerName.trim();
    if (!trimmed || !playerLeagueId) return;
    const exists = state.players.some(p => p.name.toLowerCase() === trimmed.toLowerCase() && p.league_id === playerLeagueId);
    if (exists) { setPlayerError('Este jogador já existe nesta liga.'); return; }
    const success = await onAddPlayer(trimmed, playerLeagueId);
    if (success) setPlayerName('');
  };

  // Funções de Edição
  const startEditLeague = (league: League) => {
    setEditingLeagueId(league.id);
    setEditLeagueName(league.name);
  };

  const saveEditLeague = () => {
    if (editLeagueName.trim() && editingLeagueId) {
      onUpdateLeague(editingLeagueId, editLeagueName.trim());
      setEditingLeagueId(null);
    }
  };

  const startEditMarket = (market: Market) => {
    setEditingMarketId(market.id);
    setEditMarketName(market.name);
  };

  const saveEditMarket = () => {
    if (editMarketName.trim() && editingMarketId) {
      onUpdateMarket(editingMarketId, editMarketName.trim());
      setEditingMarketId(null);
    }
  };

  const startEditPlayer = (player: Player) => {
    setEditingPlayerId(player.id);
    setEditPlayerName(player.name);
    setEditPlayerLeagueId(player.league_id);
  };

  const saveEditPlayer = () => {
    if (editPlayerName.trim() && editingPlayerId && editPlayerLeagueId) {
      onUpdatePlayer(editingPlayerId, editPlayerName.trim(), editPlayerLeagueId);
      setEditingPlayerId(null);
    }
  };

  const isSuperAdmin = state.currentUser.role === UserRole.SUPER_ADMIN;
  const isAdminOrSuper = state.currentUser.role === UserRole.ADMIN || state.currentUser.role === UserRole.SUPER_ADMIN;

  return (
    <div className="space-y-10 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black uppercase tracking-tight text-[#eaecef]">Painel de Controle</h2>
          <p className="text-white text-base mt-1 font-bold opacity-90 uppercase tracking-widest text-[10px]">Configurações globais • <span className="text-[#00ff66]">Admin Mode</span></p>
        </div>
      </div>

      {isAdminOrSuper && (
        <section className="bg-[#1e2329] rounded-[32px] border border-[#474d57] overflow-hidden shadow-2xl">
          <div className="p-8 border-b border-[#474d57] bg-gradient-to-r from-[#2b3139]/30 to-transparent flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="text-[#00ff66] drop-shadow-[0_0_5px_#00ff66]" size={24} />
              <h3 className="text-xl font-black uppercase tracking-tight text-white">Usuários do Sistema</h3>
            </div>
            <div className="relative w-full md:w-80">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#474d57]">
                <ShieldCheck size={18} />
              </div>
              <input
                type="text"
                placeholder="BUSCAR USUÁRIO..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full bg-[#0b0e11] border border-[#474d57] pl-11 pr-4 py-3 rounded-2xl text-white text-[10px] font-black tracking-widest outline-none focus:border-[#00ff66]/50 transition-all placeholder:text-[#474d57] uppercase"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="text-white uppercase text-[10px] font-black tracking-widest bg-[#0b0e11]/50">
                <tr>
                  <th className="py-5 px-8">Nome / E-mail</th>
                  <th className="py-5 px-8 text-center">Acesso</th>
                  <th className="py-5 px-8 text-center">Status</th>
                  <th className="py-5 px-8 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#474d57]/30">
                {state.users.filter(u =>
                  u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
                  (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
                ).map((user) => (
                  <tr key={user.id} className="hover:bg-[#2b3139]/30 transition-colors">
                    <td className="py-5 px-8">
                      <div className="font-black text-lg text-white uppercase">{user.name}</div>
                      <div className="text-xs text-white opacity-60 font-mono">{user.email || 'Não informado'}</div>
                    </td>
                    <td className="py-5 px-8 text-center">
                      <div className="flex justify-center">
                        {isSuperAdmin && user.id !== state.currentUser.id ? (
                          <div className="relative inline-block w-40">
                            <select
                              value={user.role}
                              onChange={(e) => onUpdateUserRole(user.id, e.target.value as UserRole)}
                              className="w-full bg-[#0b0e11] border border-[#474d57] text-white px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest appearance-none cursor-pointer focus:border-[#00ff66] outline-none"
                            >
                              <option value={UserRole.USER}>Trader</option>
                              <option value={UserRole.ADMIN}>Admin</option>
                              <option value={UserRole.SUPER_ADMIN}>Mestre</option>
                            </select>
                            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white pointer-events-none" />
                          </div>
                        ) : (
                          <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase border ${user.role === UserRole.SUPER_ADMIN ? 'bg-[#00ff66]/10 text-[#00ff66] border-[#00ff66]/30' :
                            user.role === UserRole.ADMIN ? 'bg-[#fbbf24]/10 text-[#fbbf24] border-[#fbbf24]/30' :
                              'bg-[#2b3139] text-white border-[#474d57]'
                            }`}>
                            {user.role}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-5 px-8 text-center">
                      <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase inline-flex items-center gap-2 ${user.active ? 'bg-[#00ff66]/10 text-[#00ff66]' : 'bg-[#f6465d]/10 text-[#f6465d]'}`}>
                        <div className={`w-2 h-2 rounded-full ${user.active ? 'bg-[#00ff66]' : 'bg-[#f6465d]'}`}></div>
                        {user.active ? 'Ativo' : 'Bloqueado'}
                      </span>
                    </td>
                    <td className="py-5 px-8 text-right">
                      {user.id !== state.currentUser.id && (
                        <button
                          onClick={() => onToggleUserStatus(user.id)}
                          className={`p-3 rounded-2xl border transition-all ${user.active ? 'border-[#f6465d]/30 text-[#f6465d] hover:bg-[#f6465d] hover:text-white' : 'border-[#00ff66]/30 text-[#00ff66] hover:bg-[#00ff66] hover:text-[#0b0e11]'}`}
                        >
                          {user.active ? <PowerOff size={20} /> : <Power size={20} />}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* LIGAS */}
        <div className="bg-[#1e2329] rounded-[32px] border border-[#474d57] flex flex-col shadow-xl">
          <div className="p-8 border-b border-[#474d57] flex items-center justify-between">
            <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3 text-white">
              <Trophy size={22} className="text-[#00ff66]" /> Ligas
            </h3>
            <span className="text-[10px] font-black text-white bg-[#0b0e11] px-3 py-1 rounded-lg border border-[#474d57] uppercase tracking-widest">
              {state.leagues.length} Total
            </span>
          </div>

          <div className="p-8 space-y-6">
            <div className="space-y-4">
              <div className="flex gap-3">
                <input type="text" placeholder="Nova liga..." className={`flex-1 bg-[#0b0e11] border rounded-2xl px-5 py-4 text-white outline-none transition-all font-black uppercase tracking-widest text-xs ${leagueError ? 'border-[#f6465d]' : 'border-[#474d57] focus:border-[#00ff66]'}`} value={leagueName} onChange={e => { setLeagueName(e.target.value); setLeagueError(''); }} />
                <button onClick={handleAddLeagueLocal} className="px-6 bg-[#00ff66] text-[#0b0e11] rounded-2xl font-black hover:shadow-[0_0_20px_rgba(0,255,102,0.3)] transition-all">
                  <Plus size={24} />
                </button>
              </div>
              {leagueError && <p className="text-[#f6465d] text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ml-1"><AlertCircle size={12} /> {leagueError}</p>}

              <div className="relative pt-2">
                <input
                  type="text"
                  placeholder="FILTRAR LIGAS..."
                  value={leagueSearch}
                  onChange={(e) => setLeagueSearch(e.target.value)}
                  className="w-full bg-[#0b0e11]/40 border border-[#474d57]/50 px-4 py-2.5 rounded-xl text-white text-[9px] font-black tracking-[0.2em] outline-none focus:border-[#00ff66]/30 transition-all placeholder:text-[#474d57] uppercase"
                />
              </div>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              {state.leagues.filter(l => l.name.toLowerCase().includes(leagueSearch.toLowerCase())).map(league => (
                <div key={league.id} className="flex items-center justify-between p-4 bg-[#0b0e11]/40 rounded-2xl border border-[#474d57]/30 hover:border-[#00ff66]/50 transition-all group">
                  {editingLeagueId === league.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={editLeagueName}
                        onChange={e => setEditLeagueName(e.target.value)}
                        className="flex-1 bg-[#1e2329] border border-[#00ff66] rounded-xl px-3 py-2 text-xs font-black text-white uppercase outline-none"
                      />
                      <button onClick={saveEditLeague} className="p-2 text-[#00ff66] hover:scale-110 transition-transform"><Check size={18} /></button>
                      <button onClick={() => setEditingLeagueId(null)} className="p-2 text-[#f6465d] hover:scale-110 transition-transform"><X size={18} /></button>
                    </div>
                  ) : (
                    <>
                      <span className="font-black text-xs text-white uppercase tracking-widest">{league.name}</span>
                      <button onClick={() => startEditLeague(league)} className="p-2 text-white opacity-40 hover:opacity-100 hover:text-[#00ff66] transition-all"><Edit2 size={16} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* MERCADOS */}
        <div className="bg-[#1e2329] rounded-[32px] border border-[#474d57] flex flex-col shadow-xl">
          <div className="p-8 border-b border-[#474d57] flex items-center justify-between">
            <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3 text-white">
              <Target size={22} className="text-[#00ff66]" /> Mercados
            </h3>
            <span className="text-[10px] font-black text-white bg-[#0b0e11] px-3 py-1 rounded-lg border border-[#474d57] uppercase tracking-widest">
              {state.markets.length} Total
            </span>
          </div>

          <div className="p-8 space-y-6">
            <div className="space-y-4">
              <div className="flex gap-3">
                <input type="text" placeholder="Ex: Over 2.5..." className={`flex-1 bg-[#0b0e11] border rounded-2xl px-5 py-4 text-white outline-none transition-all font-black uppercase tracking-widest text-xs ${marketError ? 'border-[#f6465d]' : 'border-[#474d57] focus:border-[#00ff66]'}`} value={marketName} onChange={e => { setMarketName(e.target.value); setMarketError(''); }} />
                <button onClick={handleAddMarketLocal} className="px-6 bg-[#00ff66] text-[#0b0e11] rounded-2xl font-black hover:shadow-[0_0_20px_rgba(0,255,102,0.3)] transition-all">
                  <Plus size={24} />
                </button>
              </div>
              {marketError && <p className="text-[#f6465d] text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ml-1"><AlertCircle size={12} /> {marketError}</p>}

              <div className="relative pt-2">
                <input
                  type="text"
                  placeholder="FILTRAR MERCADOS..."
                  value={marketSearch}
                  onChange={(e) => setMarketSearch(e.target.value)}
                  className="w-full bg-[#0b0e11]/40 border border-[#474d57]/50 px-4 py-2.5 rounded-xl text-white text-[9px] font-black tracking-[0.2em] outline-none focus:border-[#00ff66]/30 transition-all placeholder:text-[#474d57] uppercase"
                />
              </div>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              {state.markets.filter(m => m.name.toLowerCase().includes(marketSearch.toLowerCase())).map(market => (
                <div key={market.id} className="flex items-center justify-between p-4 bg-[#0b0e11]/40 rounded-2xl border border-[#474d57]/30 hover:border-[#00ff66]/50 transition-all group">
                  {editingMarketId === market.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={editMarketName}
                        onChange={e => setEditMarketName(e.target.value)}
                        className="flex-1 bg-[#1e2329] border border-[#00ff66] rounded-xl px-3 py-2 text-xs font-black text-white uppercase outline-none"
                      />
                      <button onClick={saveEditMarket} className="p-2 text-[#00ff66] hover:scale-110 transition-transform"><Check size={18} /></button>
                      <button onClick={() => setEditingMarketId(null)} className="p-2 text-[#f6465d] hover:scale-110 transition-transform"><X size={18} /></button>
                    </div>
                  ) : (
                    <>
                      <span className="font-black text-xs text-white uppercase tracking-widest">{market.name}</span>
                      <button onClick={() => startEditMarket(market)} className="p-2 text-white opacity-40 hover:opacity-100 hover:text-[#00ff66] transition-all"><Edit2 size={16} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PLAYERS */}
        <div className="bg-[#1e2329] rounded-[32px] border border-[#474d57] flex flex-col shadow-xl lg:col-span-2">
          <div className="p-8 border-b border-[#474d57] flex items-center justify-between">
            <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3 text-white">
              <UserIcon size={22} className="text-[#00ff66]" /> Players
            </h3>
            <span className="text-[10px] font-black text-white bg-[#0b0e11] px-3 py-1 rounded-lg border border-[#474d57] uppercase tracking-widest">
              {state.players.length} Total
            </span>
          </div>

          <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-6">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-white">Cadastrar Player</h4>
              <div className="space-y-4">
                <input type="text" placeholder="Nome do Jogador" className={`w-full bg-[#0b0e11] border rounded-2xl px-5 py-4 text-xs text-white outline-none transition-all font-black uppercase tracking-widest ${playerError ? 'border-[#f6465d]' : 'border-[#474d57] focus:border-[#00ff66]'}`} value={playerName} onChange={e => { setPlayerName(e.target.value); setPlayerError(''); }} />
                <select value={playerLeagueId} onChange={e => setPlayerLeagueId(e.target.value)} className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl px-5 py-4 text-xs text-white focus:border-[#00ff66] outline-none transition-all appearance-none font-black uppercase tracking-widest">
                  {state.leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <button onClick={handleAddPlayerLocal} className="w-full py-5 bg-[#00ff66] text-[#0b0e11] rounded-2xl font-black text-xs uppercase tracking-widest hover:shadow-[0_0_25px_rgba(0,255,102,0.3)] transition-all flex items-center justify-center gap-2"><Plus size={20} /> Adicionar</button>
              </div>
            </div>

            <div className="md:col-span-2 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-white">Players Ativos</h4>
                <div className="relative w-64">
                  <input
                    type="text"
                    placeholder="FILTRAR PLAYERS..."
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    className="w-full bg-[#0b0e11]/40 border border-[#474d57]/50 px-4 py-2 rounded-xl text-white text-[9px] font-black tracking-[0.2em] outline-none focus:border-[#00ff66]/30 transition-all placeholder:text-[#474d57] uppercase"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
                {state.players.filter(p => p.name.toLowerCase().includes(playerSearch.toLowerCase())).map(player => {
                  const leagueName = state.leagues.find(l => l.id === player.league_id)?.name || 'Liga';
                  return (
                    <div key={player.id} className="p-5 bg-[#0b0e11]/40 rounded-2xl border border-[#474d57]/30 hover:border-[#00ff66]/50 transition-all flex flex-col gap-1 relative group">
                      {editingPlayerId === player.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editPlayerName}
                            onChange={e => setEditPlayerName(e.target.value)}
                            className="w-full bg-[#1e2329] border border-[#00ff66] rounded-xl px-3 py-2 text-xs font-black text-white uppercase outline-none"
                          />
                          <select
                            value={editPlayerLeagueId}
                            onChange={e => setEditPlayerLeagueId(e.target.value)}
                            className="w-full bg-[#1e2329] border border-[#474d57] rounded-xl px-3 py-2 text-[10px] font-black text-white uppercase outline-none"
                          >
                            {state.leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                          </select>
                          <div className="flex justify-end gap-2 pt-1">
                            <button onClick={saveEditPlayer} className="p-2 text-[#00ff66] hover:scale-110 transition-transform"><Check size={18} /></button>
                            <button onClick={() => setEditingPlayerId(null)} className="p-2 text-[#f6465d] hover:scale-110 transition-transform"><X size={18} /></button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="font-black text-sm text-white uppercase tracking-tighter">{player.name}</span>
                            <button onClick={() => startEditPlayer(player)} className="text-white opacity-40 hover:opacity-100 hover:text-[#00ff66] transition-all"><Edit2 size={14} /></button>
                          </div>
                          <span className="text-[10px] text-[#fbbf24] font-black uppercase tracking-widest mt-1">({leagueName})</span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #0b0e11; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #474d57; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #00ff66; }
      `}</style>
    </div>
  );
};

export default AdminPanel;
